"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const moments = [{
  time: "07:00 AM",
  title: "Site Assessment",
  description: "Our team arrives on-site to review blueprints and prepare the workspace for a day of precision construction.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_assessment.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_assessment.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_assessment.png-wvc-sizes",
  alt: "Construction team reviewing blueprints on site"
}, {
  time: "09:00 AM",
  title: "Structural Framing",
  description: "The core of the project takes shape as our master carpenters execute the structural framework with exacting standards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/structural_framing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/structural_framing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/structural_framing.png-wvc-sizes",
  alt: "Carpenters working on wooden structural framing"
}, {
  time: "11:30 AM",
  title: "Quality Inspection",
  description: "Americo Moscariello personally oversees the progress, ensuring every joint and beam meets our rigorous quality benchmarks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/quality_inspection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/quality_inspection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/quality_inspection.png-wvc-sizes",
  alt: "Founder inspecting construction quality"
}, {
  time: "01:30 PM",
  title: "Property Management",
  description: "Our stewardship team conducts routine maintenance checks, ensuring every property under our care remains in pristine condition.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png-wvc-sizes",
  alt: "Property manager checking building systems"
}, {
  time: "04:00 PM",
  title: "Site Cleanup",
  description: "We believe a clean site is a safe site. Our team ensures the workspace is organized and secure before the day concludes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_cleanup.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_cleanup.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/site_cleanup.png-wvc-sizes",
  alt: "Construction site being cleaned and organized"
}, {
  time: "06:00 PM",
  title: "Project Review",
  description: "The day ends with a final walkthrough, documenting progress and preparing the schedule for the following morning.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_review.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_review.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/project_review.png-wvc-sizes",
  alt: "Architectural review of the day's progress"
}];
function TimelineMoment({
  moment,
  index
}: {
  moment: typeof moments[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isLeft = index % 2 === 0;
  return <div data-index={index} ref={ref} className="relative lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-12 items-center pl-16 lg:pl-0" style={{
    transitionDelay: `${index * 200}ms`
  }}>
      {}
      <div className={`absolute lg:left-1/2 left-[26px] top-8 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 w-3 h-3 rounded-lg z-10 transition-all duration-700 ${isVisible ? "bg-secondary scale-100 shadow-[0_0_16px_2px_var(--secondary)]" : "bg-muted scale-75"}`} style={{
      transitionDelay: `${index * 200 + 100}ms`
    }} />

      {}
      <div className={`lg:col-start-${isLeft ? "1" : "3"} lg:row-start-1 ${isLeft ? "lg:text-right lg:pr-8" : "lg:text-left lg:pl-8"} transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
      transitionDelay: `${index * 200 + 200}ms`
    }}>
        <span className="inline-block font-mono text-xs tracking-widest uppercase text-secondary border border-secondary/30 px-3 py-1 mb-4">
          {moment.time}
        </span>
        <h3 className="font-default text-2xl text-foreground mb-3 font-normal">
          {moment.title}
        </h3>
        <p className="font-default text-muted-foreground leading-relaxed text-sm max-w-md lg:max-w-none lg:inline-block">
          {moment.description}
        </p>
      </div>

      {}
      <div className="hidden lg:block lg:col-start-2 w-0" />

      {}
      <div className={`mt-6 lg:mt-0 lg:col-start-${isLeft ? "3" : "1"} lg:row-start-1 ${isLeft ? "lg:justify-self-start lg:pl-8" : "lg:justify-self-end lg:pr-8"} transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-95"}`} style={{
      transitionDelay: `${index * 200 + 300}ms`
    }}>
        <div className="w-[180px] h-[180px] lg:w-[200px] lg:h-[200px] overflow-hidden rounded-lg ring-1 ring-secondary/20">
          <img src={moment.image} data-wvc-srcset={moment.srcset} data-wvc-sizes={moment.sizes} alt={moment.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
          borderRadius: "0px"
        }} />
        </div>
      </div>
    </div>;
}
export default function Timeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="3280" id="our-journey-through-time" className="dark bg-background text-foreground py-32 lg:py-[180px] px-6 lg:px-12 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 lg:mb-[80px] transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-secondary block mb-6">
            — THE MOSCARIELLO STANDARD
          </span>
          <h2 className="font-default font-normal text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] text-foreground mb-8 tracking-tight">
            Our daily rhythm.
          </h2>
          <p className="font-default text-muted-foreground text-lg leading-relaxed max-w-xl">
            From initial site assessment to final project review — a day in the life of Moscariello Construction.
          </p>
        </div>

        {}
        <div ref={lineRef} className="relative">
          {}
          <div className="absolute lg:left-1/2 left-8 lg:-translate-x-1/2 top-0 bottom-0 w-px bg-secondary/15" />

          {}
          <div className="absolute lg:left-1/2 left-8 lg:-translate-x-1/2 top-0 w-px bg-secondary origin-top transition-transform duration-[2500ms] ease-out" style={{
          height: "100%",
          transform: lineVisible ? "scaleY(1)" : "scaleY(0)"
        }} />

          {}
          <div className="space-y-20 lg:space-y-32 relative">
            {moments.map((moment, i) => <TimelineMoment key={i} moment={moment} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}